/**
 * EarningsScreen — Écran "Mes gains" → Crédit Mytenue (programme fidélité L.521-1)
 *
 * v1.12.999 — Branché sur le système Crédit Mytenue (/credits/*) au lieu du
 * wallet CJ cashback. Affiche :
 *   - GET /credits/balance → { active(=disponible), pending, used, expired }
 *   - GET /credits/history → transactions (earn = achat partenaire,
 *                            partner_validated, use = consommation, expire)
 *
 * Les Crédits Mytenue sont NON convertibles en argent → pas de retrait PayPal.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, SafeAreaView, StatusBar, Platform,
} from 'react-native';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import BackButton from '../components/BackButton';
import { getAuthToken } from '../services/authHelpers';
import { getCreditBalance, getCreditHistory } from '../services/MytenueCredits';
import { formatRate } from '../config/CashbackConfig';

const getColors = (dark) => ({
  bg:          dark ? '#141414' : '#FAFAFA',
  card:        dark ? '#222222' : '#FFFFFF',
  gold:        '#D4AF37',
  goldDark:    dark ? '#D4B896' : '#B8860B',
  goldLight:   dark ? '#332A20' : '#FFF7E0',
  green:       '#2ECC71',
  greenLight:  dark ? '#1E3A28' : '#E8F5E9',
  orange:      '#F39C12',
  orangeLight: dark ? '#3D2F1A' : '#FFF3E0',
  blue:        dark ? '#7BAEF5' : '#1976D2',
  blueLight:   dark ? '#1A2B40' : '#E3F2FD',
  red:         '#E53935',
  text:        dark ? '#EEEEEE' : '#1A1A1A',
  textLight:   dark ? '#AAAAAA' : '#666',
  textFaint:   dark ? '#666666' : '#999',
  border:      dark ? '#383838' : '#EEE',
});

// Config d'affichage par type de transaction crédit
const getTxConfig = (COLORS, en) => ({
  earn:              { icon: '🛍️', label: en ? 'Partner purchase'  : 'Achat partenaire',  color: COLORS.green,  sign: '+' },
  partner_validated: { icon: '✓',  label: en ? 'Credit validated'  : 'Crédit validé',     color: COLORS.blue,   sign: '',  info: true },
  use:               { icon: '🎁', label: en ? 'Redemption'        : 'Utilisation',       color: COLORS.red,    sign: '−' },
  expire:            { icon: '⌛', label: en ? 'Expired'           : 'Expiré',            color: COLORS.textFaint, sign: '−' },
  grant:             { icon: '🎉', label: en ? 'Bonus'             : 'Bonus',             color: COLORS.green,  sign: '+' },
  adjust:            { icon: '⚙️', label: en ? 'Adjustment'        : 'Ajustement',        color: COLORS.textLight, sign: '' },
});

export default function EarningsScreen({ navigation }) {
  const { user } = useAuth();
  const { dark } = useTheme();
  const { lang } = useI18n();
  const en = lang === 'en';
  const COLORS = React.useMemo(() => getColors(dark), [dark]);
  const TX_CONFIG = React.useMemo(() => getTxConfig(COLORS, en), [COLORS, en]);
  const s = React.useMemo(() => makeStyles(COLORS), [COLORS]);

  const [balance,    setBalance]    = useState(null);
  const [history,    setHistory]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = await getAuthToken().catch(() => null);
      if (!token) { setBalance(null); setHistory([]); return; }
      const [bal, hist] = await Promise.all([
        getCreditBalance(token),
        getCreditHistory(token, 100),
      ]);
      setBalance(bal || {});
      setHistory(Array.isArray(hist) ? hist : []);
    } catch (e) {
      console.warn('[Earnings] load error:', e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const onRefresh = () => { setRefreshing(true); load(); };

  const formatEur = (n) => {
    const v = (Number(n) || 0).toFixed(2);
    return en ? `€${v}` : v.replace('.', ',') + ' €';
  };
  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(en ? 'en-US' : 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
  };

  const available = Number(balance?.active)  || 0; // crédits validés & utilisables
  const pending   = Number(balance?.pending) || 0; // achats partenaires non encore validés
  const used      = Number(balance?.used)    || 0;
  const expired   = Number(balance?.expired) || 0;

  if (loading) {
    return (
      <SafeAreaView style={s.loadingBox}>
        <ActivityIndicator size="large" color={COLORS.gold} />
        <Text style={s.loadingText}>{en ? 'Loading…' : 'Chargement…'}</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle={dark ? 'light-content' : 'dark-content'} />
      <View style={s.header}>
        <BackButton onPress={() => navigation.goBack()} />
        <Text style={s.title}>{en ? 'My earnings' : 'Mes gains'}</Text>
        <View style={{ width: 38 }} />
      </View>
      <ScrollView contentContainerStyle={s.content}
        refreshControl={<ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.gold} />}>
        {/* Hero : crédit disponible */}
        <View style={s.heroCard}>
          <Text style={s.heroLabel}>{en ? '✨ MYTENUE CREDITS' : '✨ CRÉDIT MYTENUE'}</Text>
          <Text style={s.heroAmount}>{formatEur(available)}</Text>
          <Text style={s.heroSub}>
            {en
              ? 'Usable in-app: subscription, upgrades, add-ons'
              : 'Utilisable dans l\'app : abonnement, upgrade, add-ons'}
          </Text>
          {pending > 0 && (
            <Text style={s.heroPending}>
              {en ? `+ ${formatEur(pending)} pending validation` : `+ ${formatEur(pending)} en attente de validation`}
            </Text>
          )}
          <View style={s.heroNote}>
            <Text style={s.heroNoteText}>
              {en
                ? 'Mytenue Credits are non-monetary loyalty points. They cannot be exchanged for cash or transferred.'
                : 'Les Crédits Mytenue sont un programme de fidélité. Non convertibles en argent, non cessibles.'}
            </Text>
          </View>
        </View>

        {/* Booster (upgrade) — sauf Gold */}
        {user?.tier !== 'gold' && (
          <View style={s.rateCard}>
            <View style={{ flex: 1 }}>
              <Text style={s.rateLabel}>{en ? 'BOOST MY REWARDS' : 'BOOSTER MES RÉCOMPENSES'}</Text>
              <Text style={s.rateSub}>
                {en
                  ? 'Upgrade to a higher plan to increase the reward earned on every purchase.'
                  : 'Passez à un plan supérieur pour augmenter la récompense reçue sur chaque achat.'}
              </Text>
            </View>
            <TouchableOpacity style={s.upgradeBtn} onPress={() => navigation.navigate('Main', { screen: 'Profil' })} activeOpacity={0.85}>
              <Text style={s.upgradeBtnText}>↑ Upgrade</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Breakdown : Pending / Disponible / Utilisé */}
        <Text style={s.sectionTitle}>{en ? 'CREDITS BREAKDOWN' : 'DÉTAIL DES CRÉDITS'}</Text>
        <View style={s.breakdownCard}>
          <View style={s.breakdownRow}>
            <View style={s.breakdownLeft}>
              <Text style={{ fontSize: 18 }}>⏳</Text>
              <View>
                <Text style={s.breakdownLabel}>{en ? 'Pending' : 'En attente'}</Text>
                <Text style={s.breakdownSub}>{en ? 'Purchases not yet validated' : 'Achats non encore validés'}</Text>
              </View>
            </View>
            <Text style={[s.breakdownAmount, { color: COLORS.orange }]}>{formatEur(pending)}</Text>
          </View>
          <View style={s.divider} />
          <View style={s.breakdownRow}>
            <View style={s.breakdownLeft}>
              <Text style={{ fontSize: 18 }}>💰</Text>
              <View>
                <Text style={s.breakdownLabel}>{en ? 'Available' : 'Disponible'}</Text>
                <Text style={s.breakdownSub}>{en ? 'Validated · usable now' : 'Validé · utilisable maintenant'}</Text>
              </View>
            </View>
            <Text style={[s.breakdownAmount, { color: COLORS.green }]}>{formatEur(available)}</Text>
          </View>
          <View style={s.divider} />
          <View style={s.breakdownRow}>
            <View style={s.breakdownLeft}>
              <Text style={{ fontSize: 18 }}>🎁</Text>
              <View>
                <Text style={s.breakdownLabel}>{en ? 'Used' : 'Utilisé'}</Text>
                <Text style={s.breakdownSub}>{en ? 'Already redeemed in-app' : 'Déjà utilisé dans l\'app'}</Text>
              </View>
            </View>
            <Text style={[s.breakdownAmount, { color: COLORS.textLight }]}>{formatEur(used)}</Text>
          </View>
          {expired > 0 && (
            <>
              <View style={s.divider} />
              <View style={s.breakdownRow}>
                <View style={s.breakdownLeft}>
                  <Text style={{ fontSize: 18 }}>⌛</Text>
                  <View>
                    <Text style={s.breakdownLabel}>{en ? 'Expired' : 'Expiré'}</Text>
                    <Text style={s.breakdownSub}>{en ? 'Past 12-month validity' : 'Validité 12 mois dépassée'}</Text>
                  </View>
                </View>
                <Text style={[s.breakdownAmount, { color: COLORS.textFaint }]}>{formatEur(expired)}</Text>
              </View>
            </>
          )}
        </View>

        {/* Historique des opérations */}
        <Text style={s.sectionTitle}>{en ? 'TRANSACTION HISTORY' : 'HISTORIQUE DES OPÉRATIONS'}</Text>
        {history.length === 0 ? (
          <View style={s.emptyBox}>
            <Text style={s.emptyIcon}>🪙</Text>
            <Text style={s.emptyTitle}>{en ? 'No transactions yet' : 'Aucune opération pour le moment'}</Text>
            <Text style={s.emptyDesc}>
              {en
                ? 'Buy via partner links in the app to earn Mytenue Credits. Your operations will appear here.'
                : 'Achetez via les liens partenaires dans l\'app pour gagner des Crédits Mytenue. Vos opérations apparaîtront ici.'}
            </Text>
          </View>
        ) : (
          history.map((tx, i) => {
            const cfg = TX_CONFIG[tx.type] || TX_CONFIG.adjust;
            const amt = Number(tx.amount) || 0;
            return (
              <View key={tx.id || i} style={s.historyItem}>
                <View style={[s.historyIcon, { backgroundColor: COLORS.goldLight }]}>
                  <Text style={{ fontSize: 20 }}>{cfg.icon}</Text>
                </View>
                <View style={{ flex: 1, gap: 3 }}>
                  <Text style={s.historyName} numberOfLines={1}>{cfg.label}</Text>
                  {tx.description ? (
                    <Text style={s.historyDesc} numberOfLines={2}>{tx.description}</Text>
                  ) : null}
                  <Text style={s.historyDate}>{formatDate(tx.created_at)}</Text>
                </View>
                <Text style={[s.historyAmount, { color: cfg.color }]}>
                  {cfg.info ? '' : `${cfg.sign} `}{formatEur(Math.abs(amt))}
                </Text>
              </View>
            );
          })
        )}

        {/* Comment ça marche */}
        <View style={s.infoBox}>
          <Text style={s.infoTitle}>{en ? 'ℹ️ How does it work?' : 'ℹ️ Comment ça marche ?'}</Text>
          <Text style={s.infoText}>
            {en
              ? `• Buy via partner links in the app\n• Credit appears as Pending within 24-48h\n• Once validated by the merchant (14-60d), it becomes Available\n• Use your Mytenue Credits for subscription, upgrades or add-ons\n• Credits expire 12 months after earning`
              : `• Achetez via les liens partenaires dans l'app\n• Le crédit apparaît En attente sous 24-48h\n• Après validation par le marchand (14-60j), il devient Disponible\n• Utilisez vos Crédits Mytenue pour l'abonnement, les upgrades ou les add-ons\n• Les crédits expirent 12 mois après leur attribution`}
          </Text>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

const makeStyles = (COLORS) => StyleSheet.create({
  safe:         { flex: 1, backgroundColor: COLORS.bg },
  loadingBox:   { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, backgroundColor: COLORS.bg },
  loadingText:  { color: COLORS.textLight, fontSize: 13 },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 14, paddingVertical: 12,
    paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) + 8 : 12,
    backgroundColor: COLORS.card, borderBottomWidth: 1, borderBottomColor: COLORS.border,
  },
  title:        { fontSize: 17, fontWeight: '600', color: COLORS.text, letterSpacing: 0.2 },
  content:      { padding: 16, gap: 16 },
  heroCard:     { backgroundColor: '#1A1A1A', borderRadius: 16, padding: 24, alignItems: 'center', borderWidth: 1.5, borderColor: COLORS.gold, gap: 6 },
  heroLabel:    { fontSize: 10, letterSpacing: 2.5, color: COLORS.gold, fontWeight: '700' },
  heroAmount:   { fontSize: 40, fontWeight: '300', color: '#fff', letterSpacing: 0.3 },
  heroSub:      { fontSize: 12, color: '#D4AF72AA' },
  heroPending:  { fontSize: 12, color: '#F39C12', fontWeight: '600', marginTop: 2 },
  heroNote:     { backgroundColor: '#FBF8F0', borderRadius: 8, padding: 10, borderWidth: 1, borderColor: '#E8DCC2', marginTop: 12 },
  heroNoteText: { fontSize: 10, color: '#7A6E55', lineHeight: 14, textAlign: 'center' },
  rateCard:     { flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.goldLight, borderWidth: 1, borderColor: '#F5DEB3', borderRadius: 12, padding: 16, gap: 12 },
  rateLabel:    { fontSize: 9, letterSpacing: 2, color: COLORS.goldDark, fontWeight: '700' },
  rateSub:      { fontSize: 11, color: '#8B6F2E', marginTop: 2 },
  upgradeBtn:   { backgroundColor: '#1A1A1A', paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: COLORS.gold },
  upgradeBtnText: { fontSize: 11, fontWeight: '700', color: COLORS.gold, letterSpacing: 0.8 },
  sectionTitle: { fontSize: 10, letterSpacing: 2, color: COLORS.textLight, fontWeight: '700', marginTop: 8, marginBottom: -4 },
  breakdownCard:{ backgroundColor: COLORS.card, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border, overflow: 'hidden' },
  breakdownRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16 },
  breakdownLeft:{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 },
  breakdownLabel:{ fontSize: 14, fontWeight: '600', color: COLORS.text },
  breakdownSub: { fontSize: 11, color: COLORS.textFaint, marginTop: 2 },
  breakdownAmount:{ fontSize: 16, fontWeight: '700' },
  divider:      { height: 1, backgroundColor: COLORS.border, marginHorizontal: 16 },
  historyItem:  { flexDirection: 'row', backgroundColor: COLORS.card, padding: 12, gap: 12, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border, alignItems: 'center' },
  historyIcon:  { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  historyName:  { fontSize: 14, fontWeight: '600', color: COLORS.text },
  historyDesc:  { fontSize: 11, color: COLORS.textLight, lineHeight: 15 },
  historyDate:  { fontSize: 10, color: COLORS.textFaint, letterSpacing: 0.3 },
  historyAmount:{ fontSize: 15, fontWeight: '700' },
  emptyBox:     { backgroundColor: COLORS.card, borderRadius: 12, padding: 32, alignItems: 'center', borderWidth: 1, borderColor: COLORS.border, gap: 8 },
  emptyIcon:    { fontSize: 40 },
  emptyTitle:   { fontSize: 15, fontWeight: '600', color: COLORS.text, marginTop: 8 },
  emptyDesc:    { fontSize: 12, color: COLORS.textLight, textAlign: 'center', lineHeight: 18 },
  infoBox:      { backgroundColor: '#EDF7FF', borderWidth: 1, borderColor: '#BBDEFB', borderRadius: 12, padding: 14 },
  infoTitle:    { fontSize: 12, fontWeight: '700', color: '#1565C0', marginBottom: 8 },
  infoText:     { fontSize: 11, color: '#3F51B5', lineHeight: 18 },
});
